import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import adminService from '@/services/adminService.ts';

interface CreateManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

interface FormState {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  company: string;
}

const EMPTY: FormState = { email: '', password: '', firstName: '', lastName: '', phone: '', company: '' };

export default function CreateManagerModal({ isOpen, onClose, onCreated }: CreateManagerModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY);
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (form.password.length < 8) {
      setError(t('superuser.admin.users.createModal.passwordMin', {
        defaultValue: 'Contraseña debe tener al menos 8 caracteres.',
      }));
      return;
    }
    if (!form.company.trim()) {
      setError(t('superuser.admin.users.createModal.companyRequired', {
        defaultValue: 'Empresa es obligatoria.',
      }));
      return;
    }

    setSubmitting(true);
    try {
      await adminService.createManager({
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim(),
      });
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('superuser.admin.users.createModal.title', { defaultValue: 'Crear Manager' })}</DialogTitle>
          <DialogDescription>
            {t('superuser.admin.users.createModal.description', {
              defaultValue: 'Provisiona la cuenta para una nueva empresa cliente.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('auth.firstNameLabel')} value={form.firstName} onChange={(v) => update('firstName', v)} required />
            <Field label={t('auth.lastNameLabel')} value={form.lastName} onChange={(v) => update('lastName', v)} required />
          </div>
          <Field label={t('auth.emailLabel')} type="email" value={form.email} onChange={(v) => update('email', v)} required />
          <Field
            label={t('auth.passwordLabel')}
            type="password"
            value={form.password}
            onChange={(v) => update('password', v)}
            placeholder={t('auth.passwordHint')}
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('auth.phoneLabel')} value={form.phone} onChange={(v) => update('phone', v)} />
            <Field
              label={t('superuser.admin.users.companyLabel', { defaultValue: 'Empresa' })}
              value={form.company}
              onChange={(v) => update('company', v)}
              required
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t('superuser.admin.users.createModal.submit', { defaultValue: 'Crear Manager' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}

function Field({ label, value, onChange, type = 'text', placeholder, required }: FieldProps) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
      />
    </div>
  );
}
